import * as React from 'react'
import { useNavigate, useLocation } from 'react-router'
import { Loader2, Sprout } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'
import { loginInputSchema } from '@/schemas/auth.schema'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useOrganizationBranding } from '@/hooks/useOrganization'

/**
 * Where to go after sign-in.
 *
 * Only a path on this app is followed. A value that does not start with a
 * single slash, starts with `//`, or contains a backslash is sent home, so a
 * crafted return cannot leave Smithwell. The connect screen depends on this:
 * its request id is a query on `/oauth/consent`, and that query is only safe
 * to keep because the path itself cannot be another site.
 */
export function resolveRedirect<S>(state: S): string {
  if (state && typeof state === 'object' && 'from' in state) {
    const from = state.from
    if (typeof from === 'string' && isSafeAppPath(from)) return from
  }
  return '/'
}

function isSafeAppPath(path: string): boolean {
  if (!path.startsWith('/')) return false
  if (path.startsWith('//')) return false
  return !path.includes('\\')
}

/** Turn an API/network failure into a message suitable for the sign-in form. */
function loginErrorMessage<E>(error: E): string {
  if (isApiError(error)) {
    if (error.status === 401) return 'Incorrect email or password.'
    return error.body.message ?? 'Unable to sign in. Please try again.'
  }
  return toErrorMessage(error)
}

/**
 * Email + password sign-in. Redirects to the attempted route on success.
 *
 * There is no "create an account" link on purpose: accounts exist only by
 * invitation, so the page tells the visitor how to get one instead of offering
 * a signup route that does not exist.
 */
export function LoginPage() {
  const branding = useOrganizationBranding()
  const organizationName = branding.data?.name ?? ''
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = React.useState<string>('')
  const [password, setPassword] = React.useState<string>('')
  const [fieldError, setFieldError] = React.useState<string | null>(null)
  const [formError, setFormError] = React.useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault()
    setFieldError(null)
    setFormError(null)

    const parsed = loginInputSchema.safeParse({ email, password })
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Invalid credentials')
      return
    }

    setIsSubmitting(true)
    try {
      await login(parsed.data)
      navigate(resolveRedirect(location.state), { replace: true })
    } catch (error) {
      setFormError(loginErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-svh items-center justify-center bg-app p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2">
          <Sprout className="h-5 w-5 text-brand" />
          <span className="font-semibold">{organizationName}</span>
        </div>

        <Card>
          <CardHeader>
            <CardTitle asChild>
              <h1>Sign in</h1>
            </CardTitle>
            <CardDescription>
              Use the email address you were invited with.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit} noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="name@smithwellorganics.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>

              {fieldError ? (
                <p className="text-sm text-destructive">{fieldError}</p>
              ) : null}
              {formError ? (
                <p className="text-sm text-destructive">{formError}</p>
              ) : null}

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                {isSubmitting ? 'Signing in\u2026' : 'Sign in'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mt-4 text-center text-xs text-muted-text">
          Need access? Ask a teammate to send you an invitation.
        </p>
      </div>
    </div>
  )
}
